import React, { useEffect, useState } from 'react';
import { SettingsFrame } from '@/modules/settings/components/SettingsFrame';
import { Hash, PhoneCall, Trash2 } from 'lucide-react';
import { EmptyState, ConfirmDialog, Input } from '@/shared/ui';
import { Badge, Button } from '@/shared/ui';
import { formatPhoneDisplay, validatePhone, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './CallNumbersPage.module.css';
import { useCallNumbers } from '../hooks/useCallNumbers';
import { useCallAgentActions } from '../hooks/useCallAgentActions';
import { useLocation } from '@/shared/hooks/useLocation';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import type { TrackedNumber } from '../api/callsApi';

function FallbackNumberCell({
  number,
  onSave,
  disabled,
}: {
  number: TrackedNumber;
  onSave: (fallbackNumber: string | null) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState(number.fallbackNumber ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setValue(number.fallbackNumber ?? ''), [number.fallbackNumber]);

  return (
    <Input
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        if (error) setError(null);
      }}
      onBlur={(e) => {
        const formatted = formatPhoneDisplay(e.target.value);
        setValue(formatted);
        const err = validatePhone(formatted);
        setError(err);
        if (err) return;
        if (formatted !== (number.fallbackNumber ?? '')) onSave(formatted || null);
      }}
      placeholder={disabled ? '—' : PHONE_PLACEHOLDER}
      error={error ?? undefined}
      disabled={disabled}
    />
  );
}

// What a caller dialling this number gets right now.
function NumberStatusBadge({ number: n }: { number: TrackedNumber }) {
  if (n.kind === 'unassigned') return <Badge variant="neutral">Unassigned</Badge>;
  if (n.kind === 'source') {
    if (n.status === 'draft') return <Badge variant="neutral">Not live yet</Badge>;
    if (n.agentStatus !== 'active') {
      return <Badge variant="warning">Not answering — agent paused</Badge>;
    }
    if (n.status === 'paused') return <Badge variant="info">Answers without the offer</Badge>;
    return <Badge variant="success">Active</Badge>;
  }
  if (n.status === 'active') return <Badge variant="success">Active</Badge>;
  if (n.status === 'paused') return <Badge variant="warning">Paused — not answering</Badge>;
  return <Badge variant="warning">Not answering — activation unfinished</Badge>;
}

export function CallNumbersPage({
  embedded = false,
  level = 2,
}: { embedded?: boolean; level?: 2 | 3 } = {}) {
  const { numbers, isLoading, isError, refetch } = useCallNumbers();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const actions = useCallAgentActions(locationId);
  const canManage = useCanManageCalls();

  const [confirmPhoneNumberId, setConfirmAgentId] = useState<string | null>(null);
  const pendingNumber = numbers.find((n) => n.id === confirmPhoneNumberId);

  return (
    <SettingsFrame
      embedded={embedded}
      level={level}
      id="settings-call-numbers"
      pageClassName={patterns.page}
      title={embedded ? 'Tracking Numbers' : 'Numbers'}
      subtitle="One pool of tracking numbers is the spine — each ties a call to its source (ad, landing page, or mailer) and is answered by the AI agent."
    >
      {isError ? (
        <EmptyState
          icon={<PhoneCall size={22} />}
          title="Couldn't load numbers"
          description="Your numbers are unaffected — try again in a moment."
          actions={
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      ) : !isLoading && numbers.length === 0 ? (
        <EmptyState
          icon={<PhoneCall size={22} />}
          title="No tracked numbers yet"
          description="Activate an agent and a tracking number is proposed in the area code you choose — nothing is bought until you approve."
        />
      ) : (
        <div className={patterns.gridTable}>
          <div
            className={`${patterns.gridTableHeader} ${styles.gridCols6} ${styles.stackedHeader}`}
          >
            <span>Number</span>
            <span>Agent</span>
            <span>Attribution source</span>
            <span>Fallback number</span>
            <span>Status</span>
            <span />
          </div>
          {numbers.map((n) => (
            <div key={n.id} className={`${patterns.gridTableRow} ${styles.gridCols6}`}>
              <div className={styles.numberCell}>
                <span className={styles.numberIcon}>
                  <Hash size={12} aria-hidden />
                </span>
                {n.number}
              </div>
              <div className={patterns.cellSecondary}>{n.agentName ?? '—'}</div>
              <div className={patterns.cellSecondary}>{n.source || '—'}</div>
              <div className={styles.fallbackCell}>
                <FallbackNumberCell
                  number={n}
                  disabled={!canManage}
                  onSave={(fallbackNumber) =>
                    actions.updateFallback.mutate({ phoneNumberId: n.id, fallbackNumber })
                  }
                />
              </div>
              <div>
                <NumberStatusBadge number={n} />
              </div>
              <div className={styles.actionCell}>
                {canManage && n.status !== 'active' && !n.isProtected && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Trash2 size={13} />}
                    onClick={() => setConfirmAgentId(n.id)}
                  >
                    Release
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmPhoneNumberId}
        onClose={() => setConfirmAgentId(null)}
        onConfirm={() => {
          if (confirmPhoneNumberId) {
            actions.deleteNumber.mutate(confirmPhoneNumberId, {
              onSuccess: () => setConfirmAgentId(null),
            });
          }
        }}
        title="Release number?"
        message={
          pendingNumber?.agentName
            ? `This permanently releases ${pendingNumber.number} from Retell — anyone who calls it (from ads, mailers, or saved contacts) will reach nothing, and you can't get the number back. ${pendingNumber.agentName} stays, without a tracking number.`
            : `This number isn't assigned to any agent and will be permanently released from Retell — you can't get it back. Anyone still calling it will reach nothing.`
        }
        confirmLabel="Release number"
        variant="destructive"
        isLoading={actions.deleteNumber.isPending}
      />

      <p className={styles.footnote}>
        The number that attributes a lead is the same number the AI agent answers — shared across
        ads, landing pages, and mailers. Fallback number: Retell rings it only when too many calls
        arrive at once for the agent to take another. A paused agent's number isn't answered at all
        — the fallback doesn't cover that.
      </p>
    </SettingsFrame>
  );
}
